import axios from 'axios';
import { auth, authCallbackError, getAccessToken } from '@/plugins/auth';

const setupAxiosRequestInterceptor = (store) => {
    axios.interceptors.request.use(async (config) => {
        if (auth.isAuthenticated?.value) {
            try {
                const accessToken = await getAccessToken();
                if (accessToken) {
                    config.headers.Authorization = `Bearer ${accessToken}`;
                }
            } catch (e) {
                // Token unavailable; let the request fly without auth and the
                // response interceptor will handle the resulting 401.
                if (import.meta.env.DEV) {
                    console.warn('Failed to get access token:', e);
                }
            }
        }

        const { state } = store;
        const hasInviteToken = Boolean(state.invites.inviteToken);

        if (hasInviteToken) {
            config.headers['x-invite-token'] = state.invites.inviteToken;
        }

        window.performance.mark(`xhr_start_${config.url}_${config.method}`);
        return config;
    }, (error) => {
        return Promise.reject(error);
    });
};

// Module-scoped guard that prevents a 401 storm (typical during boot, when
// many parallel API calls fire at once and a stale-token / wrong-audience
// rejects them all) from triggering N concurrent loginWithRedirect calls
// — which would full-page redirect to Auth0, return with a fresh code, and
// 401 again, creating an infinite reload loop. The flag resets naturally on
// the next page load (since the module is re-imported).
let redirectInProgress = false;

const triggerLoginRedirect = () => {
    if (redirectInProgress) {
        return;
    }

    // Only skip when Auth0's own login/redirect-callback returned an error
    // (captured at boot from ?error=&state=). Do NOT gate on auth.error — the
    // SDK sets that ref for every failed proxied call (including a routine
    // getAccessTokenSilently refresh failure), which would block recovery.
    if (authCallbackError) {
        return;
    }

    redirectInProgress = true;

    const currentRoute = window.location.pathname + window.location.search;
    sessionStorage.setItem('auth_redirect_target', currentRoute);

    auth.loginWithRedirect({
        appState: { target: currentRoute },
    });
};

const setupAxiosResponseInterceptor = () => {
    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const is401 = error.response?.status === 401;
            const isAuthed = auth.isAuthenticated?.value;
            const alreadyRetried = Boolean(error.config?.__authRetry);

            if (is401 && isAuthed && !alreadyRetried && !redirectInProgress) {
                // Force a non-cached token fetch and retry the original request
                // once. Covers the case where the SDK's cached access token is
                // stale (e.g. clock skew, race with backend rotation) but the
                // refresh token is still valid — avoids a full Universal Login
                // round-trip for what is really just a renewable token.
                try {
                    const token = await getAccessToken({ ignoreCache: true });
                    error.config.__authRetry = true;
                    error.config.headers = error.config.headers || {};
                    error.config.headers.Authorization = `Bearer ${token}`;

                    return await axios.request(error.config);
                } catch (renewError) {
                    if (import.meta.env.DEV) {
                        console.warn('Token renewal failed after 401, redirecting to login:', renewError);
                    }
                    triggerLoginRedirect();
                }
            }

            return Promise.reject(error);
        },
    );
};

export const setupInterceptors = (store) => {
    setupAxiosRequestInterceptor(store);
    setupAxiosResponseInterceptor();
};
